import React, { useContext, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Link, useNavigate } from 'react-router-dom';
import useApi from '../api';
import logo from '../../logo.png';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import toastConfig from '../config/Toast';
import { TeamType } from '../types';
import Team from './Team';
import useToggle from '../UseToggle';
import { useOutsideAlerter } from '../useOutsideAlerter';
import NewTeamModal from './NewTeamModal';

const Teams: React.FC = () => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [userTeams, setUserTeams] = useState<TeamType[]>([]);
    const [otherTeams, setOtherTeams] = useState<TeamType[]>([]);
    const [newTeamName, setNewTeamName] = useState('');
    const [newTeamDescription, setNewTeamDescription] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showMyTeams, setShowMyTeams] = useState(true);
    const [showOtherTeams, setShowOtherTeams] = useState(true);
    const [activeDropdownTeam, setActiveDropdownTeam] = useState<number | null>(null);

    const [dropdownVisible, setDropdownVisible] = useToggle(false); // Add this line

    const { get, getStream, post, postStream, put, patch, remove } = useApi();

    const navigate = useNavigate();

    // get url search params
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const newTeam = searchParams.get('new');

    // dropdown
    const dropdownRef = useRef(null);
    useOutsideAlerter(dropdownRef, () => clearDropdown());

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    const clearDropdown = () => {
        setActiveDropdownTeam(null);
        setDropdownVisible();
    }

    const handleEllipsisClick = (e: any, teamId: number) => {
        e.preventDefault(); // Prevent the link from navigating
        // Set the visibility of the dropdown here and set which team's dropdown is active if you have multiple teams
        activeDropdownTeam === teamId ? setActiveDropdownTeam(null) : setActiveDropdownTeam(teamId);
        setDropdownVisible();
    };

    // These classes control the width of the sidebar.
    const sidebarClass = isCollapsed ? 'w-4' : 'w-56'; // Adjust the width to your design.

    // get list of teams from server
    useEffect(() => {
        // call teams api endpoint
        const getUserTeams = async () => {
            try {
                const response = await get('/teams/?has_user=true');
                console.log(response.data)
                setUserTeams(response.data); // Set the teams data in state
            } catch (error) {
                console.error('Error fetching teams:', error);
                toast.error('Error fetching teams', toastConfig);
            }
        };
        const getNonUserTeams = async () => {
            try {
                const response = await get('/teams/?has_user=false');
                console.log(response.data)
                setOtherTeams(response.data); // Set the teams data in state
            } catch (error) {
                console.error('Error fetching teams:', error);
                toast.error('Error fetching teams', toastConfig);
            }
        }
        getUserTeams();
        getNonUserTeams();
    }, []);

    const addTeamToState = (team: TeamType) => {
        setUserTeams((prevTeams: TeamType[]) => [...prevTeams, team]);
    };

    const handleDeleteTeam = async (teamId: number) => {
        try {
            // Call the delete team API endpoint
            const response = await remove(`/teams/${teamId}`);
            // Remove the deleted team from the teams list
            setUserTeams((prevTeams: TeamType[]) => prevTeams.filter((team) => team.id !== teamId));
            // Close the dropdown
            clearDropdown();
            navigate('/teams')
            toast.success('Team deleted successfully', toastConfig);
        } catch (error) {
            console.error('Error deleting team:', error);
            toast.error('Error deleting team', toastConfig);
        }
    }

    function getTeamInitials(teamName: string) {
        const words = teamName.trim().split(/\s+/);
        if (words.length >= 2) {
            // Return the first letter of the first two words
            return words[0].charAt(0) + words[1].charAt(0);
        } else {
            // Return the first letter of the team name followed by 'T'
            return teamName.charAt(0) + 'T';
        }
    }

    // show modal if new team query param is equal to true
    useEffect(() => {
        if (newTeam === 'true') {
            setShowModal(true);
        }
    }, [newTeam]);

    return (
        <div className="">
            <div className="transform xl:translate-x-0 ease-in-out transition duration-500 flex justify-start items-start h-screen w-full sm:w-64 bg-white flex-col">
                <aside id="sidebar" className="fixed hidden z-20 h-full top-0 left-0 pt-16 flex lg:flex flex-shrink-0 flex-col w-64 transition-width duration-75 shadow-md" aria-label="Sidebar">
                    <div className="relative flex-1 flex flex-col min-h-0 border-r border-gray-200 bg-white pt-0">
                        <div className="flex flex-row border-b py-4 px-2 justify-between bg-blue-50">
                            <div className="flex flex-row items-center ml-2">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="" className="w-6 h-6 stroke-sky-800">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
                                </svg>

                                <div className="ml-4 font-semibold text-sky-800">
                                    Your Teams
                                </div>
                            </div>
                            <div className="flex flex-row">
                                <div className="p-1 mr-2 hover:bg-blue-300 hover:cursor-pointer items-center rounded-lg hover:text-white" onClick={() => setShowModal(true)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                    </svg>
                                </div>
                                <div className="p-1 mr-1" onClick={() => setShowMyTeams(!showMyTeams)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-5 h-5 transition-transform hover:cursor-pointer ${showMyTeams ? 'rotate-180' : ''}`}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                        <div className="flex-1 flex flex-col pb-4 overflow-y-auto">
                            <div className="flex-1 bg-white divide-y">
                                {showMyTeams && (
                                    <ul className="">
                                        {userTeams.map((team: any, index) => (
                                            <li key={team.id} className={`flex flex-row hover:bg-gray-100 ${index === userTeams.length - 1 ? '' : 'border-b'}`}>
                                                <Link to={`/teams/${team.id}`} className="text-base text-gray-900 font-normal flex items-center p-3 group grow">
                                                    <div className={`w-8 h-8 mr-4 flex items-center justify-center rounded-full bg-blue-500 text-white`}>
                                                        <span className="text-md font-semibold">{getTeamInitials(team.name)}</span>
                                                    </div>
                                                    <span className="flex-1 whitespace-pre-wrap">{team.name}</span>
                                                </Link>
                                                <div className="flex flex-col justify-center hover:cursor-pointer mr-2" onClick={(e) => handleEllipsisClick(e, team.id)}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="gray" className="w-6 h-6 hover:stroke-gray-700">
                                                        <path fillRule="evenodd" d="M10.5 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm0 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm0 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                                {activeDropdownTeam === team.id && (
                                                    <div ref={dropdownRef} className="absolute right-2 mt-11 py-2 w-48 bg-gray-700 rounded-md shadow-xl z-20">
                                                        <div className="block px-4 py-2 text-sm capitalize text-white hover:bg-gray-500 hover:text-white border-b">
                                                            Edit
                                                        </div>
                                                        <div onClick={() => handleDeleteTeam(team.id)} className="block px-4 py-2 text-sm capitalize text-white hover:bg-gray-500 hover:text-white">
                                                            Delete
                                                        </div>
                                                    </div>
                                                )}
                                            </li>

                                        ))}
                                    </ul>
                                )}
                                <div className="flex flex-row py-4 px-2 justify-between bg-blue-50">
                                    <div className="flex flex-row items-center ml-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 stroke-sky-800">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
                                        </svg>

                                        <div className="ml-4 font-semibold text-sky-800">
                                            Other Teams
                                        </div>
                                    </div>
                                    <div className="flex flex-row" onClick={() => setShowOtherTeams(!showOtherTeams)}>
                                        <div className="p-1 mr-1">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-5 h-5 transition-transform hover:cursor-pointer ${showOtherTeams ? 'rotate-180' : ''}`}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                                <ul className="">
                                    {showOtherTeams && otherTeams.map((team: any) => (
                                        <li key={team.id} className="flex flex-row hover:bg-gray-100 border-b">
                                            <Link to={`/teams/${team.id}`} className="text-base text-gray-900 font-normal flex items-center p-3 group grow">
                                                <div className={`w-8 h-8 mr-4 flex items-center justify-center rounded-full bg-blue-500 text-white`}>
                                                    <span className="text-md font-semibold">{getTeamInitials(team.name)}</span>
                                                </div>
                                                <span className="flex-1 whitespace-pre-wrap">{team.name}</span>
                                            </Link>
                                            <div className="flex flex-col justify-center hover:cursor-pointer mr-2" onClick={(e) => handleEllipsisClick(e, team.id)}>
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="gray" className="w-6 h-6 hover:stroke-gray-700">
                                                    <path fillRule="evenodd" d="M10.5 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm0 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm0 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            {activeDropdownTeam === team.id && (
                                                <div ref={dropdownRef} className="absolute right-2 mt-11 py-2 w-48 bg-gray-700 rounded-md shadow-xl z-20">
                                                    <div className="block px-4 py-2 text-sm capitalize text-white hover:bg-gray-500 hover:text-white border-b">
                                                        Edit
                                                    </div>
                                                    <div onClick={() => handleDeleteTeam(team.id)} className="block px-4 py-2 text-sm capitalize text-white hover:bg-gray-500 hover:text-white">
                                                        Delete
                                                    </div>
                                                </div>
                                            )}
                                        </li>

                                    ))}
                                </ul>
                            </div>

                        </div>
                    </div>
                </aside>
                <div className="bg-gray-900 opacity-50 hidden fixed inset-0 z-10" id="sidebarBackdrop"></div>

            </div>
            <NewTeamModal addTeam={addTeamToState} showModal={showModal} setShowModal={setShowModal} />
        </div>

    );
};

export default Teams;